export const EDITOR_VIEW_METADATA = {
    script: { label: 'Breakdown', navigationLabel: 'Breakdown' },
    locationscast: { label: 'Locations & Cast', navigationLabel: 'Locations & Cast' },
    synopsis: { label: 'Synopsis', navigationLabel: 'Synopsis' },
    storyboard: { label: 'Storyboard', navigationLabel: 'Storyboard' },
    moodboard: { label: 'Mood Board', navigationLabel: 'Mood Board' },
    references: { label: 'References', navigationLabel: 'References' },
    scheduling: { label: 'Stripboard', navigationLabel: 'Stripboard' },
    calendar: { label: 'Calendar', navigationLabel: 'Calendar' },
    reports: { label: 'Reports', navigationLabel: 'Summary & Reports' },
    dood: { label: 'DOOD', navigationLabel: 'DOOD' },
    sides: { label: 'Sides', navigationLabel: 'Sides' },
    shotlist: { label: 'Shot List', navigationLabel: 'Shot List' },
    callsheets: { label: 'Call Sheets', navigationLabel: 'Call Sheets', compactLabel: 'Calls' },
    vfx: { label: 'VFX Breakdown', navigationLabel: 'VFX Breakdown' },
} as const;

export type EditorView = keyof typeof EDITOR_VIEW_METADATA;
export type EditorNavGroupKey = 'script' | 'scheduling' | 'storyboards';

export interface EditorNavItem {
    readonly view: EditorView;
    readonly label: string;
}

export interface EditorNavGroup {
    readonly key: EditorNavGroupKey;
    readonly label: string;
    readonly compactLabel: string;
    readonly items: readonly EditorNavItem[];
}

const createNavItem = (view: EditorView): EditorNavItem => ({
    view,
    label: EDITOR_VIEW_METADATA[view].navigationLabel,
});

export const EDITOR_VIEWS: readonly EditorView[] = Object.freeze(
    Object.keys(EDITOR_VIEW_METADATA) as EditorView[],
);

export const EDITOR_DEFAULT_VIEW: EditorView = 'script';

export const EDITOR_VIEW_LABELS: Readonly<Record<EditorView, string>> = Object.freeze(
    EDITOR_VIEWS.reduce((labels, view) => {
        labels[view] = EDITOR_VIEW_METADATA[view].label;
        return labels;
    }, {} as Record<EditorView, string>),
);

export const EDITOR_NAV_GROUPS: readonly EditorNavGroup[] = [
    {
        key: 'script',
        label: 'Script',
        compactLabel: 'Script',
        items: [
            createNavItem('script'),
            createNavItem('locationscast'),
            createNavItem('synopsis'),
            createNavItem('reports'),
            createNavItem('vfx'),
        ],
    },
    {
        key: 'scheduling',
        label: 'Scheduling',
        compactLabel: 'Schedule',
        items: [
            createNavItem('scheduling'),
            createNavItem('calendar'),
            createNavItem('dood'),
            createNavItem('sides'),
        ],
    },
    {
        key: 'storyboards',
        label: 'Storyboards',
        compactLabel: 'Boards',
        items: [
            createNavItem('storyboard'),
            createNavItem('moodboard'),
            createNavItem('references'),
            createNavItem('shotlist'),
        ],
    },
];

export const EDITOR_CALL_SHEETS_NAV_ITEM = {
    ...createNavItem('callsheets'),
    compactLabel: EDITOR_VIEW_METADATA.callsheets.compactLabel,
} as const;

export const isEditorView = (value: unknown): value is EditorView => (
    typeof value === 'string'
    && Object.prototype.hasOwnProperty.call(EDITOR_VIEW_METADATA, value)
);

export const getEditorViewPath = (projectId: string, view: EditorView): string => (
    `/editor/${projectId}/${view}`
);
